import React, {FC, ReactElement, useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import type {richTextProps} from './RichText';


type RichTextEditorProps = {
    uniqueId: string
}

type EditorProps = richTextProps & RichTextEditorProps;

type RichTextEditorElementProps = {}


const RichTextEditorElement = styled.div<RichTextEditorElementProps>`
&.richText {
    min-height:10px;
    .highlight {
        z-index:-1;
    }
}
`;

declare global {
    interface Window {
        editorInstance: any;
        isCkeditor: boolean;
    }
}


const RichTextEditor: FC<EditorProps> = ({...props}): ReactElement => {

    const useAppSelector = props.editorPlugins.useAppSelector;

    const path = props.path;
    const useAppDispatch = props.editorPlugins.useAppDispatch;
    const Subject = props.editorPlugins.observableSubject;
    const changeValue = props.editorPlugins.changeValue;
    const mergeMap = props.editorPlugins.mergeMap;
    const debounceTime = props.editorPlugins.debounceTime;
    const from = props.editorPlugins.from;
    const trackStory = props.editorPlugins.trackStory;
    const dispatch = useAppDispatch();
    const contentObserver = new Subject();
    const contentUpdated$ = contentObserver.asObservable();
    const finalContent = (props.content && props.content.length) ? props.content : '';
    const CKEDITOR = props.editorPlugins.ckeditor;
    const [isInitialized, setEditorInitialized] = useState(false);
    let toolbar = [
        {name: 'styles', items: ['Styles']},
        {name: 'FontSize', items: ['FontSize']},
        {name: 'basicstyles', groups: ['basicstyles', 'cleanup'], items: ['Bold', 'Italic', 'Underline', 'Strike']},
        {name: 'colors', items: ['TextColor', 'BGColor', 'Highlight']},
        {name: 'paragraph', items: ['JustifyLeft', 'JustifyCenter', 'JustifyRight', 'JustifyBlock']},
        {name: 'Lineheight', items: ['lineheight']},
        {name: 'direction', items: ['BidiLtr', 'BidiRtl']},
        {name: 'list', items: ['BulletedList', 'NumberedList']},
        {name: 'links', items: ['Link', 'Unlink']},
        {name: 'Fast versioning', items: ['Fastversioning']},
        {name: 'Animated Numbers', items: ['Animated Numbers']},
        {name: 'emoji', groups: ['emoji'], items: ['EmojiPanel']}
    ];
    let editorConfig:any = {
        language: 'en',
        skin: 'storydocEditor',
        extraPlugins: 'fastversioning, emoji, sharedspace, sourcedialog, rkpi, maximize, lineheight, highlight, notification, bidi',
        forcePasteAsPlainText: true,
        allowedContent: 'u;i;em;strong;b;p;s;span[*](*){background-color,color,font-size,line-height};a[href,data-analytics-click-event];p{*};h3{*};h2{*};p[dir]{text-align};ul[dir]{text-align};ol[dir]{text-align};li[dir]{text-align};h1[dir]{text-align};h2[dir]{text-align};h3[dir]{text-align};h4[dir]{text-align};h5[dir]{text-align};h6[dir]{text-align};ul;ol;li;',
        // contentsCss: globalData?.settings?.cssUrl ? globalData.settings.cssUrl + '?' + new Date().getTime() : null,
        fontSize_sizes: '8/8px;10/10px;12/12px;14/14px;16/16px;18/18px;20/20px;22/22px;24/24px;26/26px;28/28px;30/30px;32/32px;34/34px;36/36px;38/38px;40/40px;42/42px;44/44px;46/46px;48/48px;50/50px;52/52px;54/54px;56/56px;58/58px;60/60px;62/62px;64/64px;66/66px;68/68px;70/70px;72/72px;',
        line_height: '0.75;1;1.25;1.5;1.75;2;2.5',
        stylesSet: [
            {name: 'Paragraph', element: 'p'},
            {name: 'Subtitle', element: 'h3'},
            {name: 'Title', element: 'h2'},
        ],
        startupFocus: 'end',
        title: false,
        toolbar: toolbar,
        editorplaceholder: props.placeholder
    };
    if(!props.isSettingsTool) {
        editorConfig = {
            ...editorConfig,
            toolbarLocation: 'top',
            sharedSpaces: {
                top: 'editorToolbar'
            }
        }
    }
    let warnings = null;
    const validationRules = {required: false, max: 300, errorMax: 1000, errorMessage: 'Required, up to 1000 characters.'};
    const elementRef = useRef<null | HTMLDivElement>(null);
    let isFocused = false;

    contentUpdated$.pipe(mergeMap((config: any) => {
        dispatch(trackStory({event: 'success trigger text change', screen: 'editorv4', params: {}}));
        return from([config]);
    }))
        .pipe(debounceTime(3000))
        .subscribe((config: any) => {
            updateChanges(config);
        });


    function initializeCKeditor(uniqueId: string, path: any, content: string, placeholder: string | null) {
        let checkIfEditorExist: any = elementRef.current?.querySelector('textarea.InlineEditor');
        if (checkIfEditorExist && checkIfEditorExist.nextSibling?.getAttribute('contenteditable') === 'true') return;
        if (!path) throw 'No path to rich text element';
        if (!uniqueId || !elementRef || !elementRef.current) throw 'Don\'t have access to rich text element';
        uniqueId = 'inline-editor-' + Date.now();
        elementRef.current.innerHTML = createTextArea(content, uniqueId, placeholder ? placeholder : 'Type text here...');
        CKEDITOR.inline(uniqueId, editorConfig);
        setEditorInitialized(true);
        let editor = CKEDITOR.instances[uniqueId];
        if (!editor) return;
        let counterElement = document.querySelector('.' + uniqueId + '-counter');
        let notificationsElement = document.querySelector('.' + uniqueId + '-notification');
        dispatch(trackStory({event: 'success initiate ckeditor', screen: 'editorv4', params: {}}));
        editor.on('instanceReady', function () {
            let notifications_area = document.querySelector('.cke_notifications_area');
            if (notifications_area !== null) {
                notifications_area.innerHTML = '';
            }
            warnings = checkEditorValidation(editor, validationRules, counterElement, notificationsElement);
            if (!props.isButton) manipulateCkeditorContent(editor);
        });
        editor.on('change', function () {
            warnings = checkEditorValidation(editor, validationRules, counterElement, notificationsElement);
            if (!props.isButton) manipulateCkeditorContent(editor);
        });
        editor.on('focus', function () {
            window.editorInstance = editor;
            isFocused = true;
        });
        editor.on('change', function () {
            let data = props.isButton ? editor.editable().$.innerText : editor.getData();
            contentObserver.next(data);
        });
    }

    function activateCKeditor(uniqueId: string, path: any, content: string, placeholder: string | null) {
        setTimeout(() => initializeCKeditor(uniqueId, path, content, placeholder), 300);
    }

    function createTextArea(content: string | null, uniqueId: string, placeholder: string) {
        let elementContent = content && content.length ? content : '';
        return `<textarea name="` + uniqueId + `" class="InlineEditor" placeholder="` + placeholder + `">` + elementContent + `</textarea><div class="extraData"><div class="` + uniqueId + `-counter editorCharCounter"></div><div class="` + uniqueId + `-notification` +
            ` editorNotifications"></div></div>`;
    }

    function updateChanges(data: any) {
        let changeValueData = {
            path: props.path,
            data: {
                name: props.isButton ? 'title' : 'content',
                content: data
            },
            object: 'element'
        };
        dispatch(changeValue(changeValueData));
        dispatch(trackStory({event: 'success update new text', screen: 'editorv4', params: {}}));
    }

    function manipulateCkeditorContent(editor: any) {
        if (!editor || !editor.editable().$) return;
        let editorContent = editor.editable().$;
        //update all lists markers style
        let listItems = editorContent.querySelectorAll('li');
        if (listItems) {
            listItems.forEach(function (listItem: any) {
                let spanItems = listItem.querySelectorAll('span');
                if (!spanItems || !spanItems.length) spanItems = listItem.children;
                let spanItem = spanItems[spanItems.length - 1];
                if (spanItem) {
                    if (spanItem.hasAttribute('href')) return;
                    listItem.style.color = null;
                    listItem.style.fontSize = null;
                    let spanColor = getComputedStyle(spanItem).color;
                    let spanFontSize = getComputedStyle(spanItem).fontSize;
                    if (spanColor && spanColor.length) listItem.style.color = spanColor;
                    if (spanFontSize && spanFontSize.length) listItem.style.fontSize = spanFontSize;
                } else {
                    listItem.style.color = null;
                    listItem.style.fontSize = null;
                }
            });
        }
        //update titles line height
        let headingTags = document.querySelectorAll('h1, h2, h3, h4, h5, h6, p');
        if (headingTags) {
            headingTags.forEach(function (headingTag: any) {
                let spanItems = headingTag.querySelectorAll('span');
                let spanItem = spanItems[spanItems.length - 1];
                if (spanItem) {
                    headingTag.style.fontSize = null;
                    headingTag.style.lineHeight = null;
                    let spanFontSize = getComputedStyle(spanItem).fontSize;
                    let spanLineHeight = getComputedStyle(spanItem).lineHeight;
                    if (spanFontSize && spanFontSize.length) headingTag.style.fontSize = spanFontSize;
                    if (spanLineHeight && spanLineHeight.length) headingTag.style.lineHeight = spanLineHeight;
                } else {
                    headingTag.style.fontSize = null;
                    headingTag.style.lineHeight = null;
                }
            });
        }
    }

    function checkEditorValidation(editor: any, validationRules: any, counterElement: any, notificationsElement: any) {
        let textLength = getEditorTextLength(editor.getData());
        if (validationRules.max && counterElement) {
            counterElement.innerHTML = textLength + '/' + validationRules.max;
        } else {
            counterElement.innerHTML = textLength;
        }
        var warnings = getWarnings(validationRules, textLength);
        if (warnings) {
            if (warnings === 'required') {
                notificationsElement.classList.add('required');
                warnings = 'Text is required';
                notificationsElement.innerText = warnings;
                notificationsElement.classList.add('editor-error');
                counterElement.classList.add('editor-error');
            } else {
                notificationsElement.innerText = '👉' + warnings;
                notificationsElement.classList.remove('editor-error', 'required');
                counterElement.classList.remove('editor-error');
            }
        } else {
            notificationsElement.innerText = '';
            notificationsElement.classList.remove('editor-error', 'required');
            counterElement.classList.remove('editor-error');
        }
        return warnings;
    }

    function getWarnings(validationRules: any, textLength: number) {
        if (textLength === 0 && validationRules && validationRules.required) {
            return 'required';
        }
        if (validationRules && validationRules.max && textLength >= +validationRules.max) {
            return ' Recommended max text length: ' + validationRules.max + ' characters';
        }
        if (validationRules && validationRules.min && textLength <= +validationRules.min) {
            return ' Recommended min text length: ' + validationRules.min + ' characters';
        }
        return null;
    }

    function getEditorTextLength(editorData: any) {
        const el = document.createElement('div');
        el.innerHTML = editorData;
        return el.innerText.length;
    }

    return (
        <RichTextEditorElement className={'richText content ' + (!finalContent || !finalContent.length ? 'placeholder' : '')}
                               data-path={path?.slideIndex + '-' + path?.comboIndex + '-' + path?.setIndex + '-' + path?.groupIndex + '-' + path?.elementIndex}
                               ref={elementRef}
                               onClick = {() => initializeCKeditor(props.uniqueId, path, finalContent, props.placeholder)}
                               dangerouslySetInnerHTML={{__html: finalContent || 'Start typing here...'}}
        >
        </RichTextEditorElement>
    );
};


const settings = [
    [
        {
            settingName: 'CKEditor',
        }
    ]
];


export default RichTextEditor;
